import { ReactElement, useState } from "react";
import styles from "./SharePointSettingsPage.module.less";
import {
	AlertDialog,
	Checkbox,
	FormItem,
	InputWithMoreInfo,
	RadioButtonGroup,
	RadioButtonGroupItem,
	TextInput,
	UnmanagedInlineAlertBar,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler } from "client/Forms";
import { generateTrackingId } from "client/Misc/utils";
import { SharePointSettingsFormData } from "./SharePointSettingsPage";
import {
	SharePointAuthenticationType,
	SyncClient,
	TemplateAvailabilityKind
} from "client/Api/SharePointSettings/sharepointSettings";
import { SharePointAuthenticationTypeFormItem } from "./SharePointAuthenticationTypeFormItem";

export type SettingsTabProps = {
	formHandler: IFormHandler<SharePointSettingsFormData>;
	availableAuthenticationTypes: readonly SharePointAuthenticationType[];
};

export const SettingsTab = (props: SettingsTabProps): ReactElement => {
	const {
		formHandler,
		availableAuthenticationTypes,
	} = props;

	const [isMoreLoadingInfoOpen, setIsMoreLoadingInfoOpen] = useState<boolean>(false);

	return (
		<div className={styles.tabContent}>
			<FormItem label={localize("_CompanySharePointUrl")} isRequired>
				<TextInput
					{...formHandler.for("url").attachToCheckbox()}
				/>
				<ValidationError {...formHandler.for("url").attachToValidationError()} />
			</FormItem>

			<SharePointAuthenticationTypeFormItem
				attachable={formHandler.for("authenticationType")}
				availableAuthenticationTypes={availableAuthenticationTypes}
			/>

			<FormItem
				label={localize("_TemplateAvailability")}
				isForGroup
			>
				<RadioButtonGroup
					{...formHandler.for("templateAvailabilityKind").attachToRadioButtonGroup()}
					options={templateAvailabilityOptions}
					ellipseText={false}
				/>
				<ValidationError {...formHandler.for("templateAvailabilityKind").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_LibraryManagement")}>
				<Checkbox
					{...formHandler.for("isDocumentViewStructureCustomizationEnabled").attachToCheckbox()}
					text={localize("_AllowUsersToHideShowAndReorderLibraries")}
					ellipseText={false}
				/>
				<ValidationError {...formHandler.for("isDocumentViewStructureCustomizationEnabled").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_SyncFiles")}>
				<Checkbox
					{...formHandler.for("isSyncFilesEnabled").attachToCheckbox()}
					text={localize("_IsUserAbleToSync")}
					ellipseText={false}
				/>
				<ValidationError {...formHandler.for("isSyncFilesEnabled").attachToValidationError()} />
			</FormItem>

			{formHandler.for("isSyncFilesEnabled").value && (
				<FormItem
					label={localize("_SyncFiles")}
					isForGroup
				>
					<RadioButtonGroup
						{...formHandler.for("syncClient").attachToRadioButtonGroup()}
						options={syncClientOptions}
						ellipseText={false}
					/>
					<ValidationError {...formHandler.for("syncClient").attachToValidationError()} />

					<UnmanagedInlineAlertBar
						kind="info"
						shouldBeHiddenOnMobile
					>
						{localize("_MakeSureYouUseTheSameClientAsInSharePoint")}
					</UnmanagedInlineAlertBar>
				</FormItem>
			)}

			<FormItem label={localize("_FasterLoadingExperience")}>
				<InputWithMoreInfo
					trackingId={generateTrackingId(formHandler.for("useFasterLoading").attachToCheckbox().trackingId, "__more-info")}
					onMoreInfo={() => setIsMoreLoadingInfoOpen(true)}
				>
					<Checkbox
						{...formHandler.for("useFasterLoading").attachToCheckbox()}
						text={localize("_EnableFasterLoadingExperience")}
						ellipseText={false}
					/>
				</InputWithMoreInfo>
				<ValidationError {...formHandler.for("useFasterLoading").attachToValidationError()} />

				{isMoreLoadingInfoOpen && (
					<AlertDialog
						title={localize("_FasterLoadingExperience")}
						onClose={() => setIsMoreLoadingInfoOpen(false)}
						trackingId="faster-loading-more-info-dialog"
						showCloseButton
					>
						<p>{localize("_FasterLoadingExperienceExplanationPart1")}</p>
						<p>{localize("_FasterLoadingExperienceExplanationPart2")}</p>
					</AlertDialog>
				)}
			</FormItem>

			<FormItem label={localize("_PDFAndTextFiles")}>
				<Checkbox
					{...formHandler.for("isSharePointViewerEnabled").attachToCheckbox()}
					text={localize("_OpenPDFAndTextFilesInSharePointFileViewer")}
					ellipseText={false}
				/>
				<ValidationError {...formHandler.for("isSharePointViewerEnabled").attachToValidationError()} />
			</FormItem>
		</div>
	);
};
SettingsTab.displayName = "SettingsTab";

const templateAvailabilityOptions: readonly RadioButtonGroupItem<TemplateAvailabilityKind>[] = [
	{
		text: localize("_ShowCompanyTemplates"),
		value: TemplateAvailabilityKind.Company
	},
	{
		text: localize("_ShowLibraryTemplates"),
		value: TemplateAvailabilityKind.Library
	}
];

const syncClientOptions: readonly RadioButtonGroupItem<SyncClient>[] = [
	{
		text: localize("_TheNewClient"),
		value: SyncClient.OneDrive
	},
	{
		text: localize("_TheOldClient"),
		value: SyncClient.Groove
	}
];
